import JobsViewHeader from "./JobsViewHeader";
import JobInProgressCard from "./JobInProgressCard";
import JobCompletedCard from "./JobCompletedCard";
import JobFailedCard from "./JobFailedCard";
import type { EnrichmentJob } from "wasp/entities";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { Button } from "../../components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { setJobFilter } from "../../store/filtersSlice";
import { useQuery } from "wasp/client/operations";
import { getEnrichmentTasks } from "wasp/client/operations";
import { useTranslation } from "../../i18n/hooks";

type JobsViewProps = {
  jobs: EnrichmentJob[];
};

const ITEMS_PER_PAGE = 10;

export type JobFilter = 'all' | 'running' | 'failed' | 'completed' | 'export-ready';


const JobsView = ({ jobs }: JobsViewProps) => {
  const dispatch = useDispatch();
  const activeFilter = useSelector((state: RootState) => state.filters.jobFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  
  const [jobStatuses, setJobStatuses] = useState<Record<string, string>>({});
  const jobStatusesRef = useRef<Record<string, string>>({});
  
  useEffect(() => {
    jobStatusesRef.current = jobStatuses;
  }, [jobStatuses]);
  
  const [newlyCompletedJobs, setNewlyCompletedJobs] = useState<string[]>([]);

  const [expandedCard, setExpandedCard] = useState<{
    jobId: string | null;
    rect: DOMRect | null;
    isAnimating: boolean;
  }>({
    jobId: null,
    rect: null,
    isAnimating: false
  });

  const inProgressJobs = jobs.filter(job => job.status === "in_progress");
  
  const { data: updatedJobs } = useQuery(
    getEnrichmentTasks, 
    { collectionId: inProgressJobs.length > 0 ? inProgressJobs[0].collectionId : "" }, 
    { 
      enabled: inProgressJobs.length > 0,
      refetchInterval: 5000,
      refetchOnWindowFocus: true
    }
  );
  
  const mergedJobs = useMemo(() => {
    if (!updatedJobs || updatedJobs.length === 0) return jobs;
    
    const updatedMap = new Map(updatedJobs.map(job => [job.id, job]));
    return jobs.map(job => {
      const updatedJob = updatedMap.get(job.id);
      return updatedJob ? updatedJob : job;
    });
  }, [jobs, updatedJobs]);

  useEffect(() => {
    const newStatuses: Record<string, string> = {};
    const newCompletions: string[] = [];
    
    mergedJobs.forEach(job => {
      newStatuses[job.id] = job.status as string;
      
      const previousStatus = jobStatusesRef.current[job.id];
      if (previousStatus === "in_progress" && job.status === "completed") {
        newCompletions.push(job.id);
        
        if (activeFilter === 'running') {
          dispatch(setJobFilter("export-ready"));
        }
      }
    });
    
    if (newCompletions.length > 0) {
      setNewlyCompletedJobs(prev => [...prev, ...newCompletions]);
      
      const timerId = setTimeout(() => {
        setNewlyCompletedJobs(prev => prev.filter(id => !newCompletions.includes(id)));
      }, 10000);
      
      return () => clearTimeout(timerId);
    }
    
    const hasChanges = Object.keys(newStatuses).some(id => newStatuses[id] !== jobStatusesRef.current[id]);
    if (hasChanges) {
      setJobStatuses(newStatuses);
    }
  }, [mergedJobs, activeFilter, dispatch]);

  const lastCompletedJob = mergedJobs
    .filter((job) => job.status === "completed")
    .sort(
      (a, b) =>
        new Date(b.endedAt || 0).getTime() - new Date(a.endedAt || 0).getTime()
    )[0];

  const inProgressJobCount = mergedJobs.filter(
    (job) => job.status === "in_progress"
  ).length;

  const setActiveFilter = (filter: JobFilter) => {
    dispatch(setJobFilter(filter));
  }

  const isAnyExporting = mergedJobs.some((job) => job.isExportingToShopify);

  const exportReadyCount = mergedJobs.filter(
    (job) => job.status === "completed" && !job.isExportingToShopify && !job.syncedToShopify
  ).length;

  const filteredJobs = mergedJobs.filter(job => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'export-ready') {
      return job.status === 'completed' && !job.isExportingToShopify && !job.syncedToShopify;
    }
    if (activeFilter === 'running') {
      return job.status === 'in_progress' || job.isExportingToShopify;
    }
    if (activeFilter === 'failed') return job.status === 'failed';
    if (activeFilter === 'completed') return job.status === 'completed';
    return true;
  });

 const handleCardExpand = (jobId: string, rect: DOMRect) => {
  setExpandedCard({
    jobId,
    rect,
    isAnimating: true
  });
  document.body.style.overflow = 'hidden';
};

const handleCardCollapse = () => {
  setExpandedCard(prev => ({
    ...prev,
    isAnimating: true
  }));
  
  setTimeout(() => {
    setExpandedCard({
      jobId: null,
      rect: null,
      isAnimating: false
    });
    document.body.style.overflow = '';
  }, 300);
};

  const totalPages = Math.ceil(filteredJobs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  return (
    <div className="p-6 max-w-full mx-auto space-y-6" ref={containerRef}>
      <JobsViewHeader jobs={mergedJobs} lastCompletedJob={lastCompletedJob} />

      <div className="flex gap-2 mb-4">
        <Button
          variant={activeFilter === "all" ? "default" : "outline"}
          onClick={() => setActiveFilter("all")}
        >
          {t("jobsView.all_jobs")}
        </Button>
        <Button
          variant={activeFilter === "export-ready" ? "default" : "outline"}
          className={`
            ${
              activeFilter === "export-ready"
                ? "bg-green-700 hover:bg-green-800"
                : "border-green-600 text-green-600 hover:bg-green-100 hover:text-green-700"
            } 
            transition-colors
          `}
          onClick={() => setActiveFilter("export-ready")}
          data-testid="ready-for-export-tab"
        >
          {t("jobsView.export_ready")} ({exportReadyCount})
        </Button>
        <Button
          variant={activeFilter === "running" ? "default" : "outline"}
          onClick={() => setActiveFilter("running")}
        >
          {t("jobsView.running")}
        </Button>
        <Button
          variant={activeFilter === "failed" ? "default" : "outline"}
          onClick={() => setActiveFilter("failed")}
        >
          {t("jobsView.failed")}
        </Button>
        <Button
          variant={activeFilter === "completed" ? "default" : "outline"}
          onClick={() => setActiveFilter("completed")}
        >
          {t("jobsView.completed")}
        </Button>
      </div>

      <div className="space-y-4 relative">
        {currentJobs.map((job) => {
          const isExpanded = expandedCard.jobId === job.id;
          const isNewlyCompleted = newlyCompletedJobs.includes(job.id);

          if (job.status === "completed") {
            return (
              <div key={job.id} className="relative">
                {isNewlyCompleted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm shadow-md z-10 animate-pulse">
                    {t("jobsView.newly_completed")}
                  </div>
                )}
                <JobCompletedCard
                  job={job}
                  isAnyJobExporting={mergedJobs.some(
                    (j) => j.isExportingToShopify
                  )}
                  isExpanded={isExpanded}
                  expansionRect={isExpanded ? expandedCard.rect : null}
                  onExpand={(rect) => handleCardExpand(job.id, rect)}
                  onCollapse={handleCardCollapse}
                  containerRef={containerRef}
                />
              </div>
            );
          }

          if (job.status === "failed") {
            return <JobFailedCard job={job} key={job.id} />;
          }

          return <JobInProgressCard job={job} key={job.id} />;
        })}
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className={
                  currentPage === 1
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-white cursor-pointer"
                }
              />
            </PaginationItem>

            {[...Array(totalPages)].map((_, i) => {
              const pageNumber = i + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      onClick={() => setCurrentPage(pageNumber)}
                      isActive={currentPage === pageNumber}
                      className="cursor-pointer hover:bg-white"
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }
              return null;
            })}

            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                className={
                  currentPage === totalPages
                    ? "pointer-events-none opacity-50"
                    : "hover:bg-white cursor-pointer"
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default JobsView;
